const GATEWAY_STATUSES = new Set([502, 503, 504]);

export const isLocalApi = (url) => /localhost|127\.0\.0\.1/.test(url || '');

export const isGatewayStatus = (status) => GATEWAY_STATUSES.has(status);

/**
 * Railway Serverless returns 502 when the replica is asleep. The proxy has
 * already waited ~15s by the time the browser sees that status, which is
 * usually enough for a normal wake, so the next attempt succeeds.
 *
 * A stuck wake never binds the port. Retrying it does not help. The same-origin
 * /api/wake function then redeploys the existing image, which is what actually
 * brings the replica back, and this waits that out. Local API urls skip the
 * redeploy so dev does not restart production.
 */
export async function fetchThroughSleep(url, options = {}, hooks = {}) {
  const doFetch = hooks.fetch || fetch;
  const sleep = hooks.sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const now = hooks.now || Date.now;
  const wakeUrl = hooks.wakeUrl || '/api/wake';
  const retryDelayMs = hooks.retryDelayMs ?? 500;
  const pollMs = hooks.pollMs ?? 3000;
  const deadlineMs = hooks.deadlineMs ?? 55000;
  const enableRestart = hooks.enableRestart ?? !isLocalApi(url);

  const attempt = async () => {
    try {
      return await doFetch(url, options);
    } catch {
      return null;
    }
  };

  let response = await attempt();
  if (response && !isGatewayStatus(response.status)) return response;

  hooks.onStatus?.('waking');
  await sleep(retryDelayMs);
  response = await attempt();
  if (response && !isGatewayStatus(response.status)) return response;

  if (!enableRestart) return response;

  hooks.onStatus?.('restarting');
  let wake = null;
  try {
    const wakeResponse = await doFetch(wakeUrl, { method: 'POST' });
    if (wakeResponse && typeof wakeResponse.json === 'function') {
      wake = await wakeResponse.json().catch(() => null);
    }
    if (!wakeResponse || wakeResponse.status === 404 || wake?.status === 'unconfigured' || wake?.status === 'error') {
      return response;
    }
  } catch {
    return response;
  }

  if (wake?.status === 'awake') {
    return attempt();
  }

  const deadline = now() + deadlineMs;
  while (now() < deadline) {
    await sleep(pollMs);
    response = await attempt();
    if (response && !isGatewayStatus(response.status)) return response;
  }

  return response;
}
